import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import FirstLoginPasswordChange from "@/components/FirstLoginPasswordChange";
import {
    LayoutDashboard,
    Inbox,
    Users,
    LogOut,
    Compass,
    ExternalLink,
    Sparkles,
    UserCog,
} from "lucide-react";

const NAV = [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, testid: "admin-nav-overview" },
    { to: "/admin/referrals", label: "Referrals", icon: Users, testid: "admin-nav-referrals" },
    { to: "/admin/messages", label: "Messages", icon: Inbox, testid: "admin-nav-messages" },
    { to: "/admin/visitors", label: "Visitor Insights", icon: Sparkles, testid: "admin-nav-visitors" },
    { to: "/admin/users", label: "Users", icon: UserCog, testid: "admin-nav-users" },
];

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate("/admin/login", { replace: true });
    };

    return (
        <div className="min-h-screen bg-[#F7F5F0] text-[#1A2230]" data-testid="admin-layout">
            {/* Top bar */}
            <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#F7F5F0]/85 border-b border-[#E5DFD3]">
                <div className="max-w-7xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <span className="h-9 w-9 rounded-full bg-[#0E2A47] grid place-items-center text-white">
                            <Compass className="h-4 w-4" strokeWidth={2.25} />
                        </span>
                        <div className="leading-tight">
                            <p className="font-[Outfit] text-[15px] font-semibold tracking-tight">
                                RTC · Admin
                            </p>
                            <p className="text-[11px] text-[#6F6B63] uppercase tracking-[0.14em]">
                                Internal use only
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <a
                            href="/"
                            className="hidden md:inline-flex items-center gap-1.5 text-[13px] text-[#6F6B63] hover:text-[#0E2A47]"
                            data-testid="admin-view-site"
                        >
                            <ExternalLink className="h-3.5 w-3.5" /> View site
                        </a>
                        <span className="hidden sm:inline text-[13px] text-[#6F6B63]">
                            {user?.email}
                        </span>
                        <button
                            onClick={handleLogout}
                            className="inline-flex items-center gap-1.5 rounded-full border border-[#E5DFD3] hover:border-[#0E2A47] text-[13px] px-3.5 py-1.5 transition-colors"
                            data-testid="admin-logout"
                        >
                            <LogOut className="h-3.5 w-3.5" /> Sign out
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-6 md:px-8 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Sidebar */}
                <aside className="lg:col-span-3" data-testid="admin-sidebar">
                    <nav className="flex lg:flex-col gap-1 overflow-x-auto pb-2 lg:pb-0">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.end}
                                data-testid={n.testid}
                                className={({ isActive }) =>
                                    `flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-[14px] font-medium whitespace-nowrap transition-colors ${
                                        isActive
                                            ? "bg-[#0E2A47] text-white"
                                            : "text-[#1A2230] hover:bg-[#EFEBE0]"
                                    }`
                                }
                            >
                                <n.icon className="h-4 w-4" />
                                {n.label}
                            </NavLink>
                        ))}
                    </nav>
                </aside>

                <main className="lg:col-span-9 min-w-0">
                    <Outlet />
                </main>
            </div>
            {user?.must_change_password && <FirstLoginPasswordChange />}
        </div>
    );
}
